import { createSelector } from '@reduxjs/toolkit';
import { memoize } from 'lodash-es';

import route from '@/lib/route';
import { AppState } from '@/redux/helper';
import { createSelectCanAccessHabitat } from '@/redux/selectors/users';
import store from '@/redux/store';

import { setMembersOnlyWarningModalState } from '../MembersOnlyWarningModal/redux/actions';
import { setProductsPromptModalState } from '../products/ProductsPromptModal/redux/actions';

export const buildHabitatURL = (zooSlug: string, habitatSlug: string, query?: string) =>
  `/h/${zooSlug}/${habitatSlug}${query ? `?${query}` : ''}`;

const isActive = <K extends string | number | symbol>(map: Record<K, string> | undefined, key: K) => {
  if (!map?.[key]) {
    return false;
  }
  return Date.parse(map[key]) > Date.now();
};

const selectDayPassHabitatsHistory = createSelector(
  (state: AppState) => state.user.subscription?.dayPassHabitatsHistory,
  (history) => new Set(history ?? []),
);

const selectSupportedHabitatsHistory = createSelector(
  (state: AppState) => state.user.subscription?.supportedHabitatsHistory,
  (history) => new Set(history ?? []),
);

export const createSelectProductsPromptModalState = memoize((habitatId: string | undefined) =>
  createSelector(
    selectDayPassHabitatsHistory,
    selectSupportedHabitatsHistory,
    (state: AppState) => state.user.persona,
    (state: AppState) => state.user.subscription?.featuresUntil,
    (
      dayPassHabitatsHistory,
      supportedHabitatsHistory,
      persona,
      featuresUntil,
    ): Parameters<typeof setProductsPromptModalState>[0] => {
      const isEducator = persona === 'educator';

      if (!habitatId) {
        return {
          primaryProductType: 'all-habitats-day-pass',
          forEducator: isEducator,
        };
      }

      const isExplorerFeatureExpired = featuresUntil?.explorer && !isActive(featuresUntil, 'explorer');
      // If the explorer feature was active but is now expired, show the explorer pass
      if (isExplorerFeatureExpired) {
        return {
          habitatId,
          primaryProductType: 'all-habitats-day-pass',
          secondaryProductType: 'explorer-pass',
          forEducator: isEducator,
        };
      }
      // If the habitat was accessible by day pass, show the sponsorship and single habitat day pass
      if (dayPassHabitatsHistory.has(habitatId)) {
        return {
          habitatId,
          primaryProductType: 'sponsorship',
          secondaryProductType: 'single-habitat-day-pass',
        };
      }
      // If the habitat was supported, show the single habitat day pass and sponsorship
      if (supportedHabitatsHistory.has(habitatId)) {
        return {
          habitatId,
          primaryProductType: 'single-habitat-day-pass',
          secondaryProductType: 'sponsorship',
        };
      }
      // default to all habitats day pass and single habitat day pass
      return {
        habitatId,
        primaryProductType: 'all-habitats-day-pass',
        secondaryProductType: 'single-habitat-day-pass',
        forEducator: isEducator,
      };
    },
  ),
);

export const routeToHabitat = (
  {
    id,
    slug,
    zooSlug,
    query,
  }: {
    id: string;
    slug: string;
    zooSlug: string;
    query?: string;
  },
  productsPromptModalState?: Parameters<typeof setProductsPromptModalState>[0],
) => {
  const state = store.getState();
  const canAccessHabitat = createSelectCanAccessHabitat(id)(state);
  const isLoggedIn = state.user.logged;
  if (!isLoggedIn) {
    store.dispatch(setMembersOnlyWarningModalState({ habitat: { id, slug, zooSlug }, type: 'habitat' }));
    return;
  }
  if (canAccessHabitat) {
    route(buildHabitatURL(zooSlug, slug, query));
    return;
  }
  const defaultState = createSelectProductsPromptModalState(id)(state);
  store.dispatch(setProductsPromptModalState({ ...defaultState, ...productsPromptModalState }));
};
